import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { setCenter, setZoom } from "../../store/uiSlice";
import {
  fetchUserSettings,
  updateViewOption,
  updateTooltipFields,
  setViewOptionLocal,
  setTooltipFieldsLocal,
  selectViewOptions,
  selectTooltipFields,
  initializeDefaultSettings,
} from "../../store/userSettingsSlice";

import { TooltipField, ViewOptions } from "../../types/userSettings";
import { selectViewMode } from "../../store/mapsSlice";

const ViewControls: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { language, isUtilityCardMinimized } = useSelector(
    (state: RootState) => state.ui
  );
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const viewOptions = useSelector(selectViewOptions);
  const tooltipFieldsFromStore = useSelector(selectTooltipFields);
  const viewMode = useSelector(selectViewMode);

  // Local state for tooltip fields with fallback to default
  const getDefaultTooltipFields = (): TooltipField[] => [
    {
      id: "name",
      label: language === "he" ? "שם התקן" : "Device Name",
      enabled: true,
      order: 1,
    },
    {
      id: "mac",
      label: language === "he" ? "כתובת MAC" : "MAC Address",
      enabled: false,
      order: 2,
    },
    {
      id: "rssi",
      label: language === "he" ? "עוצמת אות" : "RSSI Value",
      enabled: true,
      order: 3,
    },
    {
      id: "lastSeen",
      label: language === "he" ? "נראה לאחרונה" : "Last Seen",
      enabled: true,
      order: 4,
    },
    {
      id: "gateway",
      label: language === "he" ? "שער חזק ביותר" : "Strongest Gateway",
      enabled: false,
      order: 5,
    },
    {
      id: "status",
      label: language === "he" ? "סטטוס חיבור" : "Connection Status",
      enabled: true,
      order: 6,
    },
  ];

  const [tooltipFields, setTooltipFields] = useState<TooltipField[]>(
    tooltipFieldsFromStore || getDefaultTooltipFields()
  );

  const [showTooltipConfig, setShowTooltipConfig] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  // Initialize settings on component mount
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserSettings()).catch(() => {
        // If fetching fails (e.g., user settings don't exist yet), initialize defaults
        dispatch(initializeDefaultSettings(language));
      });
    } else {
      // For non-authenticated users, initialize default settings
      dispatch(initializeDefaultSettings(language));
    }
  }, [dispatch, isAuthenticated, language]);

  // Update local tooltip fields when store changes
  useEffect(() => {
    if (tooltipFieldsFromStore) {
      setTooltipFields(tooltipFieldsFromStore);
    }
  }, [tooltipFieldsFromStore]);

  // Fallback to default values if viewOptions is not available
  const currentViewOptions = viewOptions || {
    showDeviceNames: true,
    showGatewayCoverage: false,
    showGridLines: false,
    showRSSIValues: false,
    showLastSeen: false,
    showConfidence: false,
    autoZoom: false,
  };

  const handleTooltipFieldToggle = (fieldId: string) => {
    const updatedFields = tooltipFields.map((field) =>
      field.id === fieldId ? { ...field, enabled: !field.enabled } : field
    );
    setTooltipFields(updatedFields);

    // Update store locally for immediate UI response
    dispatch(setTooltipFieldsLocal(updatedFields));

    // Save to server if authenticated (with debouncing for better UX)
    if (isAuthenticated) {
      setTimeout(() => {
        dispatch(updateTooltipFields(updatedFields));
      }, 500);
    }
  };

  const handleDragStart = (e: React.DragEvent, fieldId: string) => {
    setDraggedItem(fieldId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetFieldId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetFieldId) return;

    const draggedIndex = tooltipFields.findIndex((f) => f.id === draggedItem);
    const targetIndex = tooltipFields.findIndex((f) => f.id === targetFieldId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newFields = [...tooltipFields];
    const [draggedField] = newFields.splice(draggedIndex, 1);
    newFields.splice(targetIndex, 0, draggedField);

    // Update order numbers
    const updatedFields = newFields.map((field, index) => ({
      ...field,
      order: index + 1,
    }));

    setTooltipFields(updatedFields);
    setDraggedItem(null);

    // Update store locally for immediate UI response
    dispatch(setTooltipFieldsLocal(updatedFields));

    // Save to server if authenticated
    if (isAuthenticated) {
      setTimeout(() => {
        dispatch(updateTooltipFields(updatedFields));
      }, 500);
    }
  };

  const handleViewOptionChange = (option: keyof ViewOptions) => {
    const newValue = !currentViewOptions[option];

    // Update store locally for immediate UI response
    dispatch(setViewOptionLocal({ key: option, value: newValue }));

    // Save to server if authenticated
    if (isAuthenticated) {
      dispatch(updateViewOption({ key: option, value: newValue }));
    }
  };

  // Map control handlers
  const handleCenterMap = () => {
    // Center map to default position (center of floor plan)
    dispatch(setCenter([300, 400])); // Center of the 600x800 floor plan
  };

  const handleResetZoom = () => {
    dispatch(setZoom(1)); // Reset to default zoom level
  };

  const handleFitToContent = () => {
    // Use component-level hooks that are already available
    // Calculate zoom to fit entire map in viewport - consider utility card visibility
    const mapHeight = 600;
    const mapWidth = 800;

    // More accurate viewport calculation that considers the utility card
    const headerHeight = 80; // Header height
    const padding = 32; // Total padding (16px * 2)
    const tabsHeight = 48; // Map tabs height (if present)

    // Calculate available width based on utility card visibility
    let availableWidth = window.innerWidth - padding;
    if (!isUtilityCardMinimized && viewMode === "tabs") {
      // When utility card is visible, map gets 2/3 of width minus gap
      availableWidth = (window.innerWidth - padding - 16) * (2 / 3); // 16px gap
    }
    // When utility card is hidden, map uses full width (already calculated above)

    const availableHeight =
      window.innerHeight - headerHeight - padding - tabsHeight;

    const zoomByHeight = availableHeight / mapHeight;
    const zoomByWidth = availableWidth / mapWidth;
    const fitZoom = Math.min(zoomByHeight, zoomByWidth, 1) * 0.8;

    console.log(
      `📏 ViewControls fit calculation: ${availableWidth}x${availableHeight} (utility minimized: ${isUtilityCardMinimized}) → zoom: ${fitZoom.toFixed(
        4
      )}`
    );

    dispatch(setCenter([300, 400])); // Center of floor plan
    dispatch(setZoom(Math.max(0.01, fitZoom)));
  };

  const handleFullScreen = () => {
    // Toggle fullscreen mode
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const resetTooltipConfig = () => {
    const defaultFields = getDefaultTooltipFields();
    setTooltipFields(defaultFields);

    // Update store
    dispatch(setTooltipFieldsLocal(defaultFields));

    // Save to server if authenticated
    if (isAuthenticated) {
      dispatch(updateTooltipFields(defaultFields));
    }
  };

  const enabledFields = tooltipFields
    .filter((f) => f.enabled)
    .sort((a, b) => a.order - b.order);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          {language === "he" ? "בקרות תצוגה" : "View Controls"}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {language === "he"
            ? "התאם את תצוגת המפה והטולטיפים"
            : "Customize map display and tooltips"}
        </p>
      </div>

      <div className="flex-1">
        {/* Map Display Options */}
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            {language === "he" ? "אפשרויות תצוגה" : "Display Options"}
          </h4>
          <div className="space-y-3">
            {(
              [
                {
                  key: "showDeviceNames" as keyof ViewOptions,
                  label:
                    language === "he" ? "הצג שמות התקנים" : "Show Device Names",
                },
                {
                  key: "showGatewayCoverage" as keyof ViewOptions,
                  label:
                    language === "he"
                      ? "הצג כיסוי שערים"
                      : "Show Gateway Coverage",
                },
                {
                  key: "showGridLines" as keyof ViewOptions,
                  label: language === "he" ? "הצג קווי רשת" : "Show Grid Lines",
                },
                {
                  key: "showRSSIValues" as keyof ViewOptions,
                  label:
                    language === "he" ? "הצג ערכי RSSI" : "Show RSSI Values",
                },
                {
                  key: "showLastSeen" as keyof ViewOptions,
                  label:
                    language === "he" ? "הצג נראה לאחרונה" : "Show Last Seen",
                },
                {
                  key: "showConfidence" as keyof ViewOptions,
                  label:
                    language === "he"
                      ? "הצג ביטחון טריאנגולציה"
                      : "Show Triangulation Confidence",
                  description:
                    language === "he"
                      ? "הצג את רמת הביטחון של מיקום ההתקן בטולטיפ"
                      : "Show device positioning confidence level in tooltips",
                },
                {
                  key: "showDeviceAsNameBox" as keyof ViewOptions,
                  label:
                    language === "he"
                      ? "הצג התקנים כתיבות שם"
                      : "Show Device Name Boxes",
                },
                // {
                //   key: "autoZoom" as keyof ViewOptions,
                //   label: language === "he" ? "זום אוטומטי" : "Auto Zoom",
                //   description:
                //     language === "he"
                //       ? "התאמת זום אוטומטית בעת בחירת התקן"
                //       : "Automatically adjust zoom when selecting devices",
                // },
              ] as const
            ).map((item) => (
              <label key={item.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={currentViewOptions[item.key]}
                  onChange={() => handleViewOptionChange(item.key)}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Tooltip Configuration */}
        {/* <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-medium text-gray-900">
              {language === "he" ? "הגדרות טולטיפ" : "Tooltip Configuration"}
            </h4>
            <button
              onClick={() => setShowTooltipConfig(!showTooltipConfig)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {showTooltipConfig
                ? language === "he"
                  ? "הסתר"
                  : "Hide"
                : language === "he"
                ? "הגדר"
                : "Configure"}
            </button>
          </div> */}

        {/* Tooltip Preview */}
        {/* <div className="mb-4 p-3 bg-gray-800 text-white rounded-md text-sm">
            <div className="font-medium mb-1">
              {language === "he" ? "תצוגה מקדימה:" : "Preview:"}
            </div>
            <div className="space-y-1">
              {enabledFields.map((field) => (
                <div key={field.id} className="flex justify-between">
                  <span>{field.label}:</span>
                  <span className="text-gray-300">
                    {field.id === "name" && "Sample Device"}
                    {field.id === "mac" && "AA:BB:CC:DD:EE:FF"}
                    {field.id === "rssi" && "-45 dBm"}
                    {field.id === "lastSeen" && "2 min ago"}
                    {field.id === "gateway" && "Gateway 1"}
                    {field.id === "status" &&
                      (language === "he" ? "מחובר" : "Online")}
                  </span>
                </div>
              ))}
            </div>
          </div> */}

        {/* Tooltip Configuration Panel */}
        {/* {showTooltipConfig && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  {language === "he" ? "שדות זמינים" : "Available Fields"}
                </span>
                <button
                  onClick={resetTooltipConfig}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  {language === "he" ? "איפוס" : "Reset"}
                </button>
              </div>

              <div className="space-y-2">
                {tooltipFields
                  .sort((a, b) => a.order - b.order)
                  .map((field) => (
                    <div
                      key={field.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, field.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, field.id)}
                      className={`flex items-center justify-between p-2 border rounded-md cursor-move transition-colors ${
                        field.enabled
                          ? "bg-blue-50 border-blue-200"
                          : "bg-gray-50 border-gray-200"
                      } ${draggedItem === field.id ? "opacity-50" : ""}`}
                    >
                      <div className="flex items-center">
                        <svg
                          className="w-4 h-4 text-gray-400 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 8h16M4 16h16"
                          />
                        </svg>
                        <span
                          className={`text-sm ${
                            field.enabled ? "text-gray-900" : "text-gray-500"
                          }`}
                        >
                          {field.label}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-400">
                          #{field.order}
                        </span>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={field.enabled}
                            onChange={() => handleTooltipFieldToggle(field.id)}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                        </label>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="text-xs text-gray-500 mt-2">
                {language === "he"
                  ? "גרור כדי לשנות סדר, סמן כדי להפעיל/לבטל"
                  : "Drag to reorder, check to enable/disable"}
              </div>
            </div>
          )}
        </div> */}

        {/* Map Controls */}
        {/* <div className="p-4 border-t border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            {language === "he" ? "בקרות מפה" : "Map Controls"}
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleCenterMap}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {language === "he" ? "מרכז מפה" : "Center Map"}
            </button>
            <button
              onClick={handleResetZoom}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {language === "he" ? "איפוס זום" : "Reset Zoom"}
            </button>
            <button
              onClick={handleFitToContent}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {language === "he" ? "התאם לתוכן" : "Fit to Content"}
            </button>
            <button
              onClick={handleFullScreen}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {language === "he" ? "מסך מלא" : "Full Screen"}
            </button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default ViewControls;
